/**
 * ElioDFU — Web Bluetooth wrapper for ELIO firmware OTA.
 *
 * Wraps `web-bluetooth-dfu` (Nordic Secure DFU over BLE) and provides a
 * simpler EventTarget-based API for the firmware-tab UI.
 *
 * Wire format reminders (verified against firmware source):
 *   - App mode advertises as     `ELIO_XXXX`        (config.h DEF_DEVICE_NAME + addr tail)
 *   - Bootloader mode advertises as `DfuTarg`       (nrf_ble_dfu.c:61, hardcoded by SDK)
 *   - Bootloader BLE address is app address + 1     (nrf_ble_dfu.c:774)
 * web-bluetooth-dfu's requestDevice(true, ...) handles the buttonless
 * trigger and the address-shifted re-discovery automatically.
 */

import SecureDfu from 'web-bluetooth-dfu';
import CRC32 from 'crc-32';
import JSZip from 'jszip';

import {triggerBootloader as triggerNusBootloader, NUS_SERVICE} from './elio-nus-trigger.js';

/** @enum {string} */
const ElioDFUError = {
    BROWSER_UNSUPPORTED: 'browser_unsupported',
    BLUETOOTH_DISABLED: 'bluetooth_disabled',
    USER_CANCELLED: 'user_cancelled',
    DEVICE_DISCONNECTED: 'device_disconnected',
    DFU_VERIFY_FAILED: 'dfu_verify_failed',
    ZIP_INVALID: 'zip_invalid',
    UNKNOWN: 'unknown'
};

/** @enum {string} */
const ElioDFUPhase = {
    IDLE: 'idle',
    LOADING_PACKAGE: 'loading_package',
    SELECTING_DEVICE: 'selecting_device',
    ENTERING_BOOTLOADER: 'entering_bootloader',
    AWAITING_RECONNECT: 'awaiting_reconnect',
    RECONNECTING: 'reconnecting',
    TRANSFERRING_INIT: 'transferring_init',
    TRANSFERRING_FIRMWARE: 'transferring_firmware',
    COMPLETED: 'completed',
    FAILED: 'failed'
};

const isWebBluetoothSupported = function () {
    return typeof navigator !== 'undefined' &&
        typeof navigator.bluetooth !== 'undefined' &&
        typeof navigator.bluetooth.requestDevice === 'function';
};

/**
 * Parses the buffer, extracts manifest/init/firmware images.
 * Returns an array of {type, initData, imageData} entries — Nordic packages
 * may contain a `softdevice_bootloader` entry plus an `application` entry.
 * @param {ArrayBuffer} zipBuffer
 * @returns {Promise<Array>}
 */
const extractDfuPackage = async function (zipBuffer) {
    let zip;
    try {
        zip = await JSZip.loadAsync(zipBuffer);
    } catch (err) {
        throw Object.assign(new Error('Unable to read ZIP'), {code: ElioDFUError.ZIP_INVALID, cause: err});
    }

    const manifestEntry = zip.file('manifest.json');
    if (!manifestEntry) {
        throw Object.assign(new Error('manifest.json missing'), {code: ElioDFUError.ZIP_INVALID});
    }
    let manifest;
    try {
        manifest = JSON.parse(await manifestEntry.async('string')).manifest;
    } catch (err) {
        throw Object.assign(new Error('manifest.json malformed'), {code: ElioDFUError.ZIP_INVALID, cause: err});
    }

    const types = ['softdevice', 'bootloader', 'softdevice_bootloader', 'application'];
    const images = [];
    for (const type of types) {
        const entry = manifest[type];
        if (!entry) continue;
        const initData = await zip.file(entry.dat_file).async('arraybuffer');
        const imageData = await zip.file(entry.bin_file).async('arraybuffer');
        images.push({type, initData, imageData});
    }
    if (images.length === 0) {
        throw Object.assign(new Error('No DFU images in package'), {code: ElioDFUError.ZIP_INVALID});
    }
    return images;
};

/**
 * Maps internal `web-bluetooth-dfu` errors to our ElioDFUError codes.
 * @param {*} err
 * @returns {string}
 */
const classifyError = function (err) {
    const msg = (err && (err.message || String(err))) || '';
    if (/User cancelled|user cancelled|cancel/i.test(msg)) return ElioDFUError.USER_CANCELLED;
    if (/disconnected|GATT/i.test(msg)) return ElioDFUError.DEVICE_DISCONNECTED;
    if (/CRC|verify/i.test(msg)) return ElioDFUError.DFU_VERIFY_FAILED;
    if (/bluetooth/i.test(msg) && /not available|disabled/i.test(msg)) return ElioDFUError.BLUETOOTH_DISABLED;
    return ElioDFUError.UNKNOWN;
};

// Filters used for both initial pick (ELIO_xxxx app mode) and post-reboot pick.
// IMPORTANT: ELIO's bootloader is customized to advertise as "ELIO_DFU" (or
// "ELIO_DFU_xxxx"), NOT the SDK default "DfuTarg". The custom name lives in
// `nRF5_SDK_13.1.0/mobilian/dfu/bootloader/ble_dfu/nrf_ble_dfu.c:61`. The iOS
// app filters with the same `ELIO_DFU` prefix.
const DFU_FILTERS = [
    {namePrefix: 'ELIO_DFU'},   // bootloader (must be FIRST so picker prioritizes)
    {namePrefix: 'ELIO_'},      // app mode — ELIO_<addr-tail>
    {namePrefix: 'ELIO'},
    {name: 'DfuTarg'}            // SDK default fallback for completeness
];

const BOOTLOADER_FILTERS = [
    {namePrefix: 'ELIO_DFU'},
    {name: 'DfuTarg'}
];

class ElioDFU extends EventTarget {
    constructor () {
        super();
        this._dfu = null;
        this._phase = ElioDFUPhase.IDLE;
        this._pendingImages = null;   // images to transfer after reconnect
    }

    static isSupported () {
        return isWebBluetoothSupported();
    }

    get phase () {
        return this._phase;
    }

    _setPhase (phase) {
        this._phase = phase;
        this.dispatchEvent(new CustomEvent('phase', {detail: {phase}}));
    }

    _emitProgress (phase, currentBytes, totalBytes) {
        const percent = totalBytes > 0 ? Math.round((currentBytes / totalBytes) * 100) : 0;
        this.dispatchEvent(new CustomEvent('progress', {
            detail: {phase, currentBytes, totalBytes, percent}
        }));
    }

    _emitError (code, message, recoverable) {
        this.dispatchEvent(new CustomEvent('error', {
            detail: {code, message, recoverable: !!recoverable}
        }));
    }

    _setupDfuClient () {
        if (this._dfu) return;
        // Inter-packet delay (ms). Default 0 hammers the firmware too fast,
        // causing CRC failures ("object failed to validate") on each 4KB
        // transfer object. nRF52 + S132 v4.0 needs ~20ms between writes for
        // stable transfer. 50,396 B / 20 B per packet * 20ms ≈ 50s — slow
        // but reliable. iOSDFULibrary defaults to similar values for older SDs.
        this._dfu = new SecureDfu(CRC32.buf, navigator.bluetooth, 20);
        this._dfu.addEventListener('log', evt => {
            // eslint-disable-next-line no-console
            if (typeof console !== 'undefined') console.log('[ElioDFU]', evt.message);
        });
        this._dfu.addEventListener('progress', evt => {
            const isInit = (evt.object || '').toLowerCase().includes('init');
            const phase = isInit ? ElioDFUPhase.TRANSFERRING_INIT : ElioDFUPhase.TRANSFERRING_FIRMWARE;
            if (this._phase !== phase) this._setPhase(phase);
            this._emitProgress(phase, evt.currentBytes, evt.totalBytes);
        });
    }

    async _transferAll (device) {
        for (const image of this._pendingImages) {
            this._setPhase(image.type === 'application' ?
                ElioDFUPhase.TRANSFERRING_FIRMWARE :
                ElioDFUPhase.TRANSFERRING_INIT);
            await this._dfu.update(device, image.initData, image.imageData);
        }
        this._pendingImages = null;
        this._setPhase(ElioDFUPhase.COMPLETED);
        this.dispatchEvent(new CustomEvent('completed'));
    }

    /**
     * Start OTA. Caller must invoke this from inside a user-gesture handler
     * so that `navigator.bluetooth.requestDevice()` can show its picker.
     *
     * The buttonless DFU flow is inherently two-step (Web Bluetooth security):
     *   step 1 — pick ELIO_* device, library writes "enter bootloader" command,
     *            device reboots; this method returns with phase=AWAITING_RECONNECT
     *   step 2 — caller invokes `continueUpdate()` from a fresh user gesture;
     *            user picks DfuTarg, firmware is transferred
     *
     * If the picked device is already in bootloader mode (DfuTarg), the transfer
     * starts immediately and step 2 is skipped.
     *
     * @param {ArrayBuffer} zipBuffer
     * @returns {Promise<void>}
     */
    async update (zipBuffer) {
        if (!isWebBluetoothSupported()) {
            this._setPhase(ElioDFUPhase.FAILED);
            this._emitError(ElioDFUError.BROWSER_UNSUPPORTED,
                'Web Bluetooth is not available in this browser.', false);
            throw new Error('Web Bluetooth not supported');
        }

        try {
            this._setPhase(ElioDFUPhase.LOADING_PACKAGE);
            this._pendingImages = await extractDfuPackage(zipBuffer);

            this._setupDfuClient();

            this._setPhase(ElioDFUPhase.SELECTING_DEVICE);
            // Diagnostic mode: localStorage.elioDfuDebug='all' → picker shows
            // ALL nearby BLE devices (helps confirm whether the board is
            // advertising at all when filters return empty).
            const debugAllDevices = (typeof localStorage !== 'undefined' &&
                localStorage.getItem('elioDfuDebug') === 'all');
            const reqOptions = debugAllDevices ?
                {acceptAllDevices: true, optionalServices: [NUS_SERVICE, SecureDfu.SERVICE_UUID]} :
                {filters: DFU_FILTERS, optionalServices: [NUS_SERVICE, SecureDfu.SERVICE_UUID]};
            if (debugAllDevices && typeof console !== 'undefined') {
                console.warn('[ElioDFU] DEBUG: showing ALL BLE devices');
            }
            const picked = await navigator.bluetooth.requestDevice(reqOptions);

            this._setPhase(ElioDFUPhase.ENTERING_BOOTLOADER);

            // If the device name starts with ELIO_DFU it's already in the
            // bootloader → skip cmd_fw_update and let SecureDfu handle it.
            const inBootloader = (picked.name || '').startsWith('ELIO_DFU') ||
                picked.name === 'DfuTarg';

            if (inBootloader) {
                if (typeof console !== 'undefined') console.log('[ElioDFU] picked already-in-bootloader device');
                const device = await this._dfu.setDfuMode(picked);
                if (!device) {
                    this._setPhase(ElioDFUPhase.AWAITING_RECONNECT);
                    return;
                }
                await this._transferAll(device);
                return;
            }

            // App-mode device. Use the iOS/Android-proven cmd_fw_update path.
            if (typeof console !== 'undefined') console.log('[ElioDFU] picked app-mode device, trying NUS cmd_fw_update');
            try {
                await triggerNusBootloader(picked, msg => {
                    if (typeof console !== 'undefined') console.log('[ElioDFU/NUS]', msg);
                });
            } catch (e) {
                if (typeof console !== 'undefined') {
                    console.warn('[ElioDFU/NUS] failed:', e.message);
                }
                // Fallback only if connection is still alive — otherwise the
                // user has to retry from a fresh requestDevice anyway.
                if (picked.gatt.connected) {
                    if (typeof console !== 'undefined') console.log('[ElioDFU] falling back to SDK Buttonless');
                    const device = await this._dfu.setDfuMode(picked);
                    if (device) {
                        await this._transferAll(device);
                        return;
                    }
                }
            }

            this._setPhase(ElioDFUPhase.AWAITING_RECONNECT);
        } catch (err) {
            const code = err.code || classifyError(err);
            this._setPhase(ElioDFUPhase.FAILED);
            this._emitError(code, err.message || String(err), code !== ElioDFUError.BROWSER_UNSUPPORTED);
            throw err;
        }
    }

    /**
     * Continue OTA after the device rebooted into bootloader mode.
     * Must be called from a fresh user gesture (button click).
     *
     * Filters narrowed to `DfuTarg` only — picking the app device here would
     * just re-trigger the buttonless command and put us in a loop. If DfuTarg
     * is not visible in the picker, the device probably never finished entering
     * bootloader (most often because `sd_softdevice_disable()` failed in the
     * app's `bootloader_start()` while the BLE connection was still active).
     */
    async continueUpdate () {
        if (!this._pendingImages) {
            this._emitError(ElioDFUError.UNKNOWN, 'Nothing to continue', false);
            return;
        }
        try {
            this._setupDfuClient();
            this._setPhase(ElioDFUPhase.RECONNECTING);
            const device = await this._dfu.requestDevice(true, BOOTLOADER_FILTERS);
            if (!device) {
                this._setPhase(ElioDFUPhase.AWAITING_RECONNECT);
                this._emitError(ElioDFUError.DEVICE_DISCONNECTED,
                    'Bootloader (ELIO_DFU) not found in picker. Power-cycle the device and start over.',
                    true);
                return;
            }
            await this._transferAll(device);
        } catch (err) {
            const code = err.code || classifyError(err);
            this._setPhase(ElioDFUPhase.FAILED);
            this._emitError(code, err.message || String(err), true);
            throw err;
        }
    }
}

export {ElioDFU as default, ElioDFUError, ElioDFUPhase, extractDfuPackage};
